import React, { lazy, Suspense } from "react";
import { SEOHead } from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import { LazySection } from "@/components/LazySection";
import {
  TrendingUp,
  CalendarCheck,
  MessageSquare,
  Sparkles,
  ShieldCheck,
  Wrench,
  BarChart3,
  Camera,
  Star,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const Footer = lazy(() => import("@/components/Footer"));

const services = [
  {
    icon: Camera,
    title: "Listing Optimization",
    description:
      "Professional photography coordination, SEO-optimized titles and descriptions, strategic keyword placement, and multi-platform positioning to rank at the top of Airbnb, VRBO, and Booking.com search results.",
    highlights: [
      "Pro photo coordination",
      "SEO-optimized copy",
      "Multi-platform syndication",
      "Competitor keyword analysis",
    ],
  },
  {
    icon: TrendingUp,
    title: "AI-Driven Dynamic Pricing",
    description:
      "Daily price adjustments powered by AI that analyzes local events, competitor occupancy, seasonality, and demand signals. We don't set prices once and forget — we optimize every single day.",
    highlights: [
      "Daily rate updates",
      "Event & demand tracking",
      "Competitor benchmarking",
      "Revenue maximization",
    ],
  },
  {
    icon: MessageSquare,
    title: "24/7 Guest Communication",
    description:
      "Lightning-fast responses to every booking inquiry, pre-arrival question, and in-stay request — around the clock. Guests receive hotel-level service, which drives 5-star reviews and repeat bookings.",
    highlights: [
      "<5 min response time",
      "Pre-arrival automation",
      "In-stay support",
      "Check-out follow-up",
    ],
  },
  {
    icon: Sparkles,
    title: "Cleaning & Turnover Management",
    description:
      "Coordinated professional turnovers between every stay with strict quality control checklists. Every clean is inspected, inventory is verified, and your property is reset to 5-star standards.",
    highlights: [
      "Professional cleaning crew",
      "Quality control inspection",
      "Inventory verification",
      "Supply restocking",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Guest Screening & Security",
    description:
      "Every guest is vetted before booking is confirmed. We use identity verification, review history screening, and NoiseAware monitoring during stays to protect your property from parties and damage.",
    highlights: [
      "Identity verification",
      "Review history check",
      "NoiseAware monitoring",
      "Party prevention",
    ],
  },
  {
    icon: Wrench,
    title: "Maintenance & Repairs",
    description:
      "Proactive property inspections catch issues before guests do. When repairs are needed, we coordinate trusted contractors fast — so small problems don't become expensive ones.",
    highlights: [
      "Routine inspections",
      "Emergency coordination",
      "Trusted contractor network",
      "Owner approval workflow",
    ],
  },
  {
    icon: CalendarCheck,
    title: "Calendar & Compliance Management",
    description:
      "Multi-platform calendar syncing across Airbnb, VRBO, and Booking.com eliminates double bookings. We also monitor local STR permit compliance and renewal deadlines for your county.",
    highlights: [
      "Multi-platform sync",
      "Double-booking prevention",
      "Permit compliance tracking",
      "Regulatory updates",
    ],
  },
  {
    icon: BarChart3,
    title: "Financial Reporting",
    description:
      "Transparent monthly owner statements with revenue breakdowns, expense summaries, and occupancy metrics. Reports are structured for easy handoff to your CPA at tax time.",
    highlights: [
      "Monthly statements",
      "Revenue & expense breakdown",
      "Occupancy metrics",
      "Tax-ready documentation",
    ],
  },
];

const ServicesPage = () => {
  const scrollToForm = () => {
    window.location.href = "/#lead-form";
  };

  return (
    <div className="min-h-screen bg-background font-body">
      <SEOHead
        title="Full-Service Airbnb Management | JNN Homes"
        description="From pricing optimization to 24/7 guest support and compliance management, discover our comprehensive Airbnb management services in VA & DC."
        canonical="https://www.jnnhomes.com/services"
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Airbnb Property Management",
          provider: {
            "@type": "LocalBusiness",
            name: "JNN Homes",
            url: "https://www.jnnhomes.com",
            telephone: "+17038905425",
            areaServed: [
              "Alexandria City, VA",
              "Arlington, VA",
              "Fairfax County, VA",
              "Loudoun County, VA",
              "Prince William County, VA",
              "Washington, DC",
            ],
          },
          description:
            "Full-service Airbnb management including listing optimization, dynamic pricing, 24/7 guest communication, cleaning, guest screening, maintenance, calendar sync, and financial reporting.",
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 bg-gradient-to-br from-background via-background to-muted/50 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] opacity-20 pointer-events-none bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/40 via-transparent to-transparent" />
        <div className="container relative mx-auto px-4 text-center max-w-4xl">
          <div className="inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 text-sm font-semibold text-primary mb-6">
            <Star className="w-4 h-4 fill-current" />
            Full-Service Airbnb Management
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground leading-tight mb-6">
            Everything Handled.
            <br />
            <span className="text-primary italic">
              You Just Collect Income.
            </span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
            From listing creation to guest checkout — and every detail in
            between — JNN Homes runs your short-term rental as a high-performing
            investment asset.
          </p>
          <Button
            onClick={scrollToForm}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-lg px-8 py-6 h-auto rounded-full"
          >
            See What My Property Can Earn
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-background" id="services">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16 space-y-3">
            <h2 className="text-3xl md:text-4xl font-heading font-bold">
              Our Complete Service Stack
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">
              Eight core disciplines that maximize revenue and protect your
              asset — all included in every management tier.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {services.map((service, i) => {
              const Icon = service.icon;
              return (
                <div
                  key={i}
                  className="bg-card border border-border rounded-2xl p-8 hover:border-primary/30 transition-colors group"
                >
                  <div className="flex gap-5 mb-5">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground self-center">
                      {service.title}
                    </h3>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-5">
                    {service.description}
                  </p>
                  <ul className="grid grid-cols-2 gap-2">
                    {service.highlights.map((h, j) => (
                      <li
                        key={j}
                        className="flex items-center gap-2 text-xs text-muted-foreground"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Band */}
      <section className="py-20 bg-primary/10 border-y border-primary/20">
        <div className="container mx-auto px-4 text-center max-w-2xl">
          <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
            Ready to Start Earning More?
          </h2>
          <p className="text-muted-foreground mb-8">
            Submit your property details and we'll run a free revenue projection
            for your VA or DC home.
          </p>
          <Button
            onClick={scrollToForm}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-lg px-8 py-6 h-auto rounded-full w-full max-w-sm"
          >
            See My Airbnb Income
          </Button>
        </div>
      </section>

      <LazySection
        minHeight="160px"
        rootMargin="200px"
        fallback={<div className="h-40 bg-card/20" />}
      >
        <Suspense fallback={<div className="h-40 bg-card/20" />}>
          <Footer />
        </Suspense>
      </LazySection>
    </div>
  );
};

export default ServicesPage;
